<template>
  <on-click-outside :do="close">
    <div class="relative">
      <!-- loading icon -->
      <span
        v-if="isLoading"
        class="absolute z-10 inset-y-0 right-5 inline-flex items-center"
      >
        <svg
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </span>
      <!-- end loading icon -->
      <!-- clear button -->
      <button
        v-if="value"
        @click="clear"
        type="button"
        class="absolute z-10 inset-y-0 right-8 focus:outline-none"
        tabindex="-1"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-gray-300 hover:text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
      <!-- end clear button -->
      <!-- autocomplete button -->
      <button
        :disabled="isDisabled"
        ref="button"
        @click="open"
        type="button"
        :class="
          isDisabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'
        "
        class="
          relative
          w-full
          bg-white
          border border-gray-300
          shadow-sm
          rounded-md
          pl-3
          pr-14
          py-2
          text-left
          focus:outline-none
          focus:ring-1
          focus:ring-cyan-500
          focus:border-cyan-500
          text-base
        "
      >
        <span v-if="value" class="block truncate">
          {{ value[itemTextKey] }}
        </span>
        <span v-else class="block truncate">
          {{ label }}
        </span>
        <span
          class="
            absolute
            inset-y-0
            right-0
            flex
            items-center
            pr-2
            pointer-events-none
          "
        >
          <svg
            class="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </button>
      <!-- end autocomplete button -->
      <!-- dropdown list menu -->
      <transition
        leave-active-class="transition ease-in duration-100"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-show="isOpen"
          :class="`
            absolute
            z-20
            ${position === 'bottom' ? 'mt-1' : '-bottom-0 mb-12'}
            w-full
            bg-white
            shadow-lg
            rounded-md
            ring-1 ring-black ring-opacity-5`"
        >
          <div v-if="isSearch" class="p-2 border-b">
            <input
              ref="search"
              v-model="search"
              @keydown.esc="close"
              @keydown.up="highlightPrev"
              @keydown.down="highlightNext"
              @keydown.enter.prevent="selectHighlighted"
              @keydown.tab.prevent
              type="text"
              class="
                focus:ring-cyan-500 focus:border-cyan-500
                block
                w-full
                border-gray-300
                shadow-sm
                rounded-md
                py-1.5
              "
              placeholder="Search"
            />
          </div>
          <ul
            ref="options"
            v-show="filteredOptions.length > 0"
            class="max-h-56 overflow-auto py-1 text-base"
          >
            <li
              class="
                relative
                pl-3
                pr-9
                py-2
                text-gray-900
                cursor-default
                select-none
              "
              :class="{ 'bg-cyan-600 text-white': i === highlightedIndex }"
              v-for="(option, i) in filteredOptions"
              :key="i"
              @click="select(option, i)"
              @mouseover="highlightedIndex = i"
            >
              <span
                class="block truncate"
                :class="{
                  'text-white': i === highlightedIndex,
                  'font-semibold': value
                    ? option[itemValueKey] === value[itemValueKey]
                    : false,
                }"
                >{{ option[itemTextKey] }}
              </span>
              <span
                class="
                  absolute
                  inset-y-0
                  right-0
                  pr-4
                  text-cyan-600
                  flex
                  items-center
                "
              >
                <svg
                  v-show="
                    value ? option[itemValueKey] === value[itemValueKey] : false
                  "
                  class="h-5 w-5"
                  :class="{ 'text-white': i === highlightedIndex }"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </li>
          </ul>
          <div
            v-show="filteredOptions.length === 0"
            class="px-3 py-4 text-gray-600"
          >
            No results found for "{{ search }}"
          </div>
        </div>
      </transition>
      <!-- end dropdown list menu -->
    </div>
  </on-click-outside>
</template>

<script>
import OnClickOutside from '@/components/OnClickOutside'

export default {
  name: 'AppAutoComplete',

  components: {
    OnClickOutside,
  },

  props: {
    position: {
      type: String,
      default: 'bottom',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    isSearch: {
      type: Boolean,
      default: false,
    },

    options: {
      type: Array,
      required: true,
    },

    label: {
      type: String,
      required: true,
    },

    filterFunction: {
      type: Function,
    },

    value: {
      type: Object,
    },

    itemTextKey: {
      type: String,
      required: true,
    },

    itemValueKey: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      search: '',
      highlightedIndex: 0,
    }
  },

  computed: {
    filteredOptions() {
      if (this.filterFunction) {
        return this.filterFunction(this.search, this.options)
      }

      return this.options.filter((option) => {
        return option[this.itemTextKey]
          .toString()
          .toLowerCase()
          .includes(this.search.toLowerCase())
      })
    },
  },

  methods: {
    open() {
      if (this.isOpen) {
        this.isOpen = false
        return
      }

      this.isOpen = true
      this.search = ''

      if (this.value) {
        const index = this.options.findIndex(
          (el) => el[this.itemValueKey] === this.value[this.itemValueKey]
        )
        if (index !== -1) {
          this.highlightedIndex = index
        }
      }

      this.$nextTick(() => {
        if (this.isSearch) this.$refs.search.focus()
        this.scrollToHighlighted()
      })
    },

    close() {
      if (!this.isOpen) {
        return
      }

      this.isOpen = false
      // this.$refs.button.focus()
    },

    clear() {
      this.$emit('input', null)
    },

    select(option, i) {
      this.$emit('input', option)
      this.highlightedIndex = i
      this.close()
    },

    selectHighlighted() {
      this.select(
        this.filteredOptions[this.highlightedIndex],
        this.highlightedIndex
      )
    },

    scrollToHighlighted() {
      this.$refs.options.children[this.highlightedIndex].scrollIntoView({
        block: 'nearest',
      })
    },

    highlight(index) {
      this.highlightedIndex = index

      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.filteredOptions.length - 1
      }

      if (this.highlightedIndex > this.filteredOptions.length - 1) {
        this.highlightedIndex = 0
      }

      this.scrollToHighlighted()
    },

    highlightPrev() {
      this.highlight(this.highlightedIndex - 1)
    },

    highlightNext() {
      this.highlight(this.highlightedIndex + 1)
    },
  },
}
</script>
